/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  CardHeader,
} from "@mui/material";

import { Spacer } from "../utils/Spacer";
import { IntegratedSystemDetail } from "../../api/integratedsystems";
import { ComplianceMap } from "../compliance/ComplianceMap";
import styled from "@emotion/styled";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { DashboardHealthScore } from "./DashboardHealthScore";
import { ChangeEntityMetric } from "../../api/suggestedchanges";
import { NUMBER_FORMATTER } from "../utils/utils";
import { AutomaticAndManualLineChart } from "./AutomaticAndManualLineChart";
import { CRMOverviewPanel } from "./CRMOverviewPanel";

const MapContainer = styled.div`
  height: 344px;
`;

interface DashboardStatsProps {
  systemDetails: IntegratedSystemDetail;
  scoreStats: any;
  writtenChanges: any;
  isLoading: boolean;
  changeEntityMetrics: ChangeEntityMetric[];
  systemType?: string;
}

export const DashboardReporting: React.FC<DashboardStatsProps> = React.memo(
  ({
    systemDetails,
    scoreStats,
    writtenChanges,
    isLoading,
    changeEntityMetrics,
    systemType,
  }) => {
    const contactChangeMetrics = changeEntityMetrics?.filter(
      (item) => item.change_entity === "CONTACT"
    );
    const accountChangeMetrics = changeEntityMetrics?.filter(
      (item) => item.change_entity === "ACCOUNT"
    );

    const numberFormatterOrZero = (parentArray, metricName) => {
      const value = parentArray && parentArray[0] && parentArray[0][metricName];
      if (value) {
        return NUMBER_FORMATTER.format(value);
      }
      return 0;
    };

    return (
      <>
        <Grid justifyContent="space-between" container spacing={6}>
          <Grid>
            <Typography variant="h4" gutterBottom>
              Reporting
            </Typography>
          </Grid>
        </Grid>
        <Spacer />
        <Grid container spacing={6}>
          <Grid
            size={{
              xs: 12,
              lg: 12,
            }}
          >
            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={6}>
                {/* Row 1: CRM Overview, Automated and Manual Changes, Data Changes Overview */}
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 3,
                  }}
                >
                  <CRMOverviewPanel systemDetails={systemDetails} />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 5,
                  }}
                >
                  <AutomaticAndManualLineChart
                    writtenChanges={writtenChanges}
                    systemType={systemType}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 4,
                  }}
                >
                  <MiraCard sx={{ height: "100%" }}>
                    <CardHeader title={"Data Changes Overview"} />
                    <MiraCardContent>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell></TableCell>
                            <TableCell align="right">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Contacts
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Companies
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Total
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                contactChangeMetrics,
                                "total"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                accountChangeMetrics,
                                "total"
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Changes
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                contactChangeMetrics,
                                "total_changes"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                accountChangeMetrics,
                                "total_changes"
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Automated
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                contactChangeMetrics,
                                "automated_changes"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                accountChangeMetrics,
                                "automated_changes"
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              <Typography
                                variant={"caption"}
                                style={{ fontWeight: "bold" }}
                              >
                                Manual
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                contactChangeMetrics,
                                "manual_changes"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {numberFormatterOrZero(
                                accountChangeMetrics,
                                "manual_changes"
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </MiraCardContent>
                  </MiraCard>
                </Grid>

                {/* Row 2: Contacts Location, Health Score */}
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 7,
                  }}
                >
                  <MiraCard>
                    <CardHeader title={"Contacts Location"} />
                    <MiraCardContent>
                      <MapContainer>
                        <ComplianceMap
                          systemDetails={systemDetails}
                          onMarkerClick={(event, index) => {
                            console.log(event);
                          }}
                        />
                      </MapContainer>
                    </MiraCardContent>
                  </MiraCard>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 12,
                    md: 5,
                  }}
                >
                  <DashboardHealthScore scoreStats={scoreStats} />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </>
    );
  }
);
